<template>
  <div class="flexing flex-col w-full">
    <form class="w-full p-6 space-y-6" @submit.prevent>
      <h1 class="text-texts-standard-important font-medium text-36">
        {{ $t('passwordResetEmail.passwordResetSubject') }}
      </h1>
      <p class="text-texts-standard-default text-28 font-medium">
        {{ $t('settings.enterNewPassword') }}
      </p>
      <div>
        <div class="v-field--variant-outlined relative">
          <input
            v-model.trim="pwdInput"
            v-focus
            :class="{ 'is-error': errors.password.size > 0 }"
            :type="isPasswordVisible ? 'text' : 'password'"
            :placeholder="$t('homepage.password')"
            :disabled="isLoading"
          />
          <div class="v-field__outline" v-html="inputLabel('homepage.password')" />
          <div
            class="input-icon cursor-pointer absolute top-1/2 transform -translate-y-1/2 right-16"
            :class="isPasswordVisible ? 'is-password-visible-icon' : 'is-password-hidden-icon'"
            @click="isPasswordVisible = !isPasswordVisible"
          />
          <tippy
            class="absolute top-1/2 transform -translate-y-1/2 right-4"
            theme="WSM"
            placement="top"
          >
            <div class="input-icon is-info-icon cursor-pointer" />
            <template #content>
              <p>{{ passwordTooltip }}</p>
            </template>
          </tippy>
        </div>
        <div class="validation-text__wrapper">
          <div
            class="validation-text"
            :class="{ 'validation-text--error': errors.password.size > 0 }"
          >
            <span v-for="(error, i) in errors.password" :key="i">
              {{ error }}
            </span>
          </div>
        </div>
      </div>
      <div>
        <div class="v-field--variant-outlined relative">
          <input
            v-model.trim="pwdConfirmationInput"
            :class="{ 'is-error': errors.passwordConfirmation.size > 0 }"
            :type="isPasswordConfirmationVisible ? 'text' : 'password'"
            :placeholder="$t('settings.repeatNewPassword')"
            :disabled="isLoading"
          />
          <div class="v-field__outline" v-html="inputLabel('settings.repeatNewPassword')" />
          <div
            class="input-icon cursor-pointer absolute top-1/2 transform -translate-y-1/2 right-4"
            :class="isPasswordVisible ? 'is-password-visible-icon' : 'is-password-hidden-icon'"
            @click="isPasswordConfirmationVisible = !isPasswordConfirmationVisible"
          />
        </div>
        <div class="validation-text__wrapper">
          <div
            class="validation-text"
            :class="{ 'validation-text--error': errors.passwordConfirmation.size > 0 }"
          >
            <span v-for="(error, i) in errors.passwordConfirmation" :key="i">
              {{ error }}
            </span>
          </div>
        </div>
      </div>
      <app-button
        type="submit"
        btn-type="confirm"
        :disabled="isSubmitDisabled"
        @click="$emit('submit')"
      >
        {{ $t('settings.confirm') }}
        <component-loading class="absolute inset-0" :is-loading="isLoading" />
      </app-button>
      <p class="text-texts-standard-additional text-28">
        {{ $t('homepage.rememberPassword') }}
        <span
          class="text-texts-standard-default font-bold uppercase underline cursor-pointer ml-2"
          @click="$emit('login')"
        >
          {{ $t('homepage.loginBtn') }}
        </span>
      </p>
    </form>
  </div>
</template>

<script lang="ts">
import ComponentLoading from '../GlobalComponents/ComponentLoading.vue'
import { getConnectedAccountStatusText } from '@/helpers'
import { defineComponent, type PropType } from 'vue'
import type { FormErrors } from '@/interfaces/login/Login'
import { PASSWORD_MIN_LENGTH, PASSWORD_MAX_LENGTH } from '@/data/login/constants'

interface ComponentData {
  PASSWORD_MIN_LENGTH: typeof PASSWORD_MIN_LENGTH
  PASSWORD_MAX_LENGTH: typeof PASSWORD_MAX_LENGTH
  isPasswordVisible: boolean
  isPasswordConfirmationVisible: boolean
  pwdInput: string
  pwdConfirmationInput: string
}

export default defineComponent({
  components: {
    ComponentLoading,
  },
  props: {
    password: {
      type: String,
      required: true,
    },
    passwordConfirmation: {
      type: String,
      required: true,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    errors: {
      type: Object as PropType<FormErrors>,
      default: () => {},
    },
  },
  emits: ['update:password', 'update:passwordConfirmation', 'submit', 'login'],
  data(): ComponentData {
    return {
      PASSWORD_MIN_LENGTH,
      PASSWORD_MAX_LENGTH,
      isPasswordVisible: false,
      isPasswordConfirmationVisible: false,
      pwdInput: this.password,
      pwdConfirmationInput: this.passwordConfirmation,
    }
  },
  computed: {
    passwordTooltip(): string {
      return this.$replacePlaceholder(
        this.$replacePlaceholder(
          this.$t('homepage.errorPasswordInvalidFormat'),
          /%s/,
          this.PASSWORD_MIN_LENGTH.toString(),
        ),
        /%s/,
        this.PASSWORD_MAX_LENGTH.toString(),
      )
    },
    isSubmitDisabled(): boolean {
      return (
        this.isLoading ||
        this.password.length < PASSWORD_MIN_LENGTH ||
        this.password.length > PASSWORD_MAX_LENGTH ||
        this.passwordConfirmation.length < PASSWORD_MIN_LENGTH ||
        this.passwordConfirmation.length > PASSWORD_MAX_LENGTH ||
        this.errors.password.size > 0 ||
        this.errors.passwordConfirmation.size > 0
      )
    },
  },
  watch: {
    pwdInput(): void {
      this.$emit('update:password', this.pwdInput)
    },
    pwdConfirmationInput(): void {
      this.$emit('update:passwordConfirmation', this.pwdConfirmationInput)
    },
  },
  methods: {
    getConnectedAccountStatusText,
    inputLabel(translatedLabel: string): string {
      return `
        <div class="v-field__outline__start"></div>
        <div class="v-field__outline__notch">
          <label class="v-label v-field-label" aria-hidden="true">
            ${this.$t(translatedLabel)}
          </label>
        </div>
        <div class="v-field__outline__end"></div>
      `
    },
  },
})
</script>
